import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store/hooks';
import { setExperienceLive } from '../../store/slices/exhibitSlice';
import TcButton from '../TcButton';
import TcPaper from '../TcPaper';
import TcImage from '../TcImage';
import TcScanQrCodeBar from '../TcScanQrCodeBar';
import LoadingScreen from '../shared/LoadingScreen';
import { useSimpleImagePreloader } from '../../hooks/useImagePreloader';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';
import { scrollToTop } from '../../utils/functions';

import imageSG6000 from '../../assets/images/exhibit/sg60/img_sg60_00.jpg';
import imageSG6001 from '../../assets/images/exhibit/sg60/img_sg60_01.jpg';
import imageSG6002 from '../../assets/images/exhibit/sg60/img_sg60_02.jpg';
import imageSG6003 from '../../assets/images/exhibit/sg60/img_sg60_03.jpg';



export default function SG60() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [step, setStep] = useState<'intro' | 'welcomeToSG'>('intro');

    // Device detection
    const { orientation } = useDeviceDetection();
    const isLandscape = orientation === 'landscape';

    // Preload all images when component mounts
    const allImages = [
        imageSG6000,
        imageSG6001,
        imageSG6002,
        imageSG6003
    ];

    const { isLoading, isComplete } = useSimpleImagePreloader(allImages);
    
    const handleContinue = () => {
        setStep('welcomeToSG');
        
        setTimeout(() => {
            const rootElement = document.getElementsByClassName('main-content-wrapper')[0] as HTMLElement;
            if (rootElement) scrollToTop(rootElement);
        }, 0);
    };

    // Show loading state while images are being cached
    if (isLoading) {
        return <LoadingScreen message="Loading images..." />;
    }
    
    // Introduction screen
    if (isComplete && step === 'intro') {
        return (
            <div className={`
                main-content-wrapper bg-black 
                ${isLandscape ? '!pt-8' : '!pt-[112px]'} 
                unselectable 
                has-transition 
            `}>
                <TcPaper classNameStyle="mt-4 mb-6">
                    <h1>{t('exhibits.sg60.title')}</h1>
                </TcPaper>

                <TcImage classNameStyle="mb-6">
                    <img
                        src={imageSG6000}
                        alt={t('exhibits.sg60.title')}
                        className="w-full"
                    />
                </TcImage>

                <TcPaper classNameStyle="general-content">
                    <p
                        dangerouslySetInnerHTML={{ __html: t('exhibits.sg60.description') }}
                    />
                </TcPaper>

                <TcImage classNameStyle="mb-6">
                    <a 
                        href="https://www.sg60.gov.sg/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block cursor-pointer hover:opacity-90 transition-opacity"
                    >
                        <img
                            src={imageSG6001}
                            alt={t('exhibits.sg60.title')}
                            className="w-full"
                        />
                    </a>
                </TcImage>

                <div className="button-wrapper px-6 pb-6 !max-w-lg has-transition">
                    <TcButton
                        handleClick={handleContinue}
                        primary
                    >
                        {t('common.continue')}
                    </TcButton>
                </div>

                <TcScanQrCodeBar classNameStyle="fixed bottom-0 left-0 right-0 z-[20]" />
            </div>
        );
    }
    
    // Welcome to Singapore view
    return (
        <div className={`
            main-content-wrapper bg-black 
            ${isLandscape ? '!pt-8' : '!pt-[112px]'} 
            unselectable 
            has-transition 
        `}>
            <TcPaper classNameStyle="mt-4 mb-6">
                <h1>{t('exhibits.sg60.welcomeToSG')}</h1>
            </TcPaper>

            <TcImage classNameStyle="mb-6">
                <img
                    src={imageSG6002}
                    alt={t('exhibits.sg60.title')}
                    className="w-full"
                />
            </TcImage>

            <TcPaper classNameStyle="general-content">
                <p
                    dangerouslySetInnerHTML={{ __html: t('exhibits.sg60.welcomeToSGDescription') }}
                />
                <p 
                    className="mt-6 mb-2 text-xl text-[var(--color-blue-list-item-highlight)]"
                    dangerouslySetInnerHTML={{ __html: t('exhibits.sg60.continueToLevel2') }}
                />
            </TcPaper>

            <TcImage classNameStyle="mb-6">
                <img
                    src={imageSG6003}
                    alt={t('exhibits.sg60.title')}
                    className="w-full"
                />
            </TcImage>

            <TcScanQrCodeBar classNameStyle="fixed bottom-0 left-0 right-0 z-[20]" />
        </div>
    );
} 